import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Modal, OverlayTrigger, Pagination, Tooltip} from '@themesberg/react-bootstrap';
import {FormattedMessage} from 'react-intl';
import {useHistory} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faAngleDoubleLeft, faAngleDoubleRight, faCalendarPlus, faCoins, faEye, faLock, faMagnifyingGlass,
  faPen, faPlus, faSort, faSortDown, faSortUp, faTimes, faTrashAlt, faUserCheck, faUserPlus, faUsers
} from '@fortawesome/free-solid-svg-icons';
import API from '../services/API';
import Utils from '../services/Utils';
import {usePlan} from '../services/PlanContext';
import PageHeader from '../components/PageHeader';
import CustomerFormModal from '../components/CustomerFormModal';
import CustomerDetailPanel from '../components/CustomerDetailPanel';
import BookingCreateModal from '../components/BookingCreateModal';
import BookingModal from '../components/BookingModal';
import ExportDropdown from '../components/ExportDropdown';
import {
  avatarColor, customerInitials, customerName, formatDate, formatMoney, matchesCustomer, normalizeText
} from '../services/customers';

// Bascule tableau (desktop) / cartes (mobile), comme Réservations.
const MOBILE_QUERY = '(max-width: 767.98px)';
const useIsMobile = () => {
  const get = () => (typeof window !== 'undefined' && window.matchMedia)
    ? window.matchMedia(MOBILE_QUERY).matches : false;
  const [isMobile, setIsMobile] = React.useState(get);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(MOBILE_QUERY);
    const handler = () => setIsMobile(mql.matches);
    handler();
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, []);
  return isMobile;
};

const PAGE_SIZE = 25;
const HOVER = ['hover'];
const intentClass = (intent) => ({info: 'is-info', danger: 'is-danger'}[intent] || '');

const FILTERS = [
  {key: 'ALL', labelId: 'FILTER_ALL', test: () => true},
  {key: 'REGULAR', labelId: 'FILTER_REGULAR', test: c => c.stats.ridesCompleted >= 2},
  {key: 'UPCOMING', labelId: 'FILTER_UPCOMING', test: c => c.stats.ridesUpcoming > 0},
  {key: 'NO_RIDE', labelId: 'FILTER_NO_RIDE', test: c => c.stats.ridesCompleted === 0},
];

// Tri côté client : la liste complète est déjà chargée (statistiques comprises).
const SORTS = {
  name: c => `${normalizeText(c.lastName)} ${normalizeText(c.firstName)}`,
  rides: c => c.stats.ridesCompleted,
  lastRide: c => c.stats.lastRideAt || 0,
  totalSpent: c => c.stats.totalSpent,
  createdAt: c => c.createdAt || 0,
};
const SORT_COLUMNS = [
  {column: 'createdAt', labelId: 'CUSTOMER_SINCE'},
  {column: 'name', labelId: 'CUSTOMER'},
  {column: 'rides', labelId: 'RIDES_COMPLETED'},
  {column: 'lastRide', labelId: 'LAST_RIDE'},
  {column: 'totalSpent', labelId: 'TOTAL_SPENT'},
];

export default (props) => {
  Utils.setBackground(false);

  const {intl} = props;
  const history = useHistory();
  const {openSubscription} = usePlan();
  const isMobile = useIsMobile();

  const [customers, setCustomers] = useState(null);
  const [currency, setCurrency] = useState('EUR');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);

  const [formCustomer, setFormCustomer] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bookingCustomer, setBookingCustomer] = useState(null);
  const [focusedBooking, setFocusedBooking] = useState(null);
  const [showFocusedBooking, setShowFocusedBooking] = useState(false);
  const [lengthUnit, setLengthUnit] = useState('KM');

  const refreshCustomers = useCallback(() => {
    API.getCustomers(history).then(response => {
      if (response && response.data.success === true) {
        setCustomers(response.data.data.customers);
        setCurrency(response.data.data.currency || 'EUR');
      } else {
        setCustomers(list => list || []);
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  }, [history, intl]);

  // Une modification depuis la fiche (réservation, édition, fusion) rafraîchit la liste ET la fiche.
  const refreshAll = useCallback(() => {
    refreshCustomers();
    setDetailRefreshKey(key => key + 1);
  }, [refreshCustomers]);

  useEffect(() => {
    if (!Utils.isAccessToken(history)) return;
    refreshCustomers();
  }, []);

  // ── KPIs (clients visibles uniquement : un client masqué n'a pas de statistiques) ──
  const kpis = useMemo(() => {
    const list = (customers || []).filter(c => !c.locked);
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const buyers = list.filter(c => c.stats.ridesCompleted > 0);
    const revenue = buyers.reduce((sum, c) => sum + c.stats.totalSpent, 0);
    return {
      total: (customers || []).length,
      newThisMonth: list.filter(c => (c.createdAt || 0) >= monthStart).length,
      regular: list.filter(c => c.stats.ridesCompleted >= 2).length,
      averageRevenue: buyers.length ? revenue / buyers.length : 0,
    };
  }, [customers]);

  // ── Recherche + filtre + tri (les clients masqués restent en fin de liste) ──
  const visible = useMemo(() => {
    const activeFilter = FILTERS.find(f => f.key === filter) || FILTERS[0];
    const getter = SORTS[sortBy] || SORTS.createdAt;
    const direction = sortOrder === 'asc' ? 1 : -1;
    return (customers || [])
      .filter(c => (c.locked ? filter === 'ALL' && !query.trim() : activeFilter.test(c) && matchesCustomer(c, query)))
      .sort((a, b) => {
        if (a.locked !== b.locked) return a.locked ? 1 : -1;
        if (a.locked) return 0;
        const va = getter(a);
        const vb = getter(b);
        if (va < vb) return -direction;
        if (va > vb) return direction;
        return 0;
      });
  }, [customers, query, filter, sortBy, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = visible.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => setPage(1), [query, filter, sortBy, sortOrder]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(order => (order === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder(column === 'name' ? 'asc' : 'desc');
    }
  };

  // ── Actions ──
  const openDetail = (customer) => {
    if (customer.locked) {
      openSubscription();
      return;
    }
    setDetailId(customer.id);
    setShowDetail(true);
  };
  const closeDetail = useCallback(() => setShowDetail(false), []);

  const openForm = (customer) => {
    setFormCustomer(customer || null);
    setShowForm(true);
  };

  const handleSaved = () => {
    setShowForm(false);
    refreshAll();
  };

  const handleDelete = () => {
    const target = deleteTarget;
    API.deleteCustomer(history, target.id).then(response => {
      if (response && response.data.success === true) {
        Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'CUSTOMER_DELETED'}));
        setDeleteTarget(null);
        if (detailId === target.id) setShowDetail(false);
        refreshCustomers();
      } else if (response && response.data.data === 'BOOKING_LOCKED') {
        setDeleteTarget(null);
        openSubscription();
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  };

  const openBooking = (booking, unit) => {
    setLengthUnit(unit);
    setFocusedBooking(booking);
    setShowFocusedBooking(true);
  };

  const actionsFor = (c) => [
    {key: 'details', icon: faEye, labelId: 'VIEW_DETAILS', onClick: () => openDetail(c)},
    {key: 'booking', icon: faCalendarPlus, labelId: 'NEW_BOOKING', intent: 'info', onClick: () => setBookingCustomer(c)},
    {key: 'edit', icon: faPen, labelId: 'EDIT', onClick: () => openForm(c)},
    {key: 'delete', icon: faTrashAlt, labelId: 'DELETE', intent: 'danger', onClick: () => setDeleteTarget(c)},
  ];

  // ── Rendu ──
  const lockedLabel = (
    <button type="button" className="ccv2-booking-locked" title={intl.formatMessage({id: 'LOCKED_CUSTOMER_HINT'})}
            onClick={(e) => { e.stopPropagation(); openSubscription(); }}>
      <FontAwesomeIcon icon={faLock}/> <FormattedMessage id="LOCKED_CUSTOMER"/>
    </button>
  );

  const Identity = ({c}) => (
    <div className="ccv2-cust-cell-identity">
      <span className="ccv2-cust-avatar" style={{background: c.locked ? '#98A1B5' : avatarColor(c.id)}}>
        {c.locked ? <FontAwesomeIcon icon={faLock}/> : customerInitials(c)}
      </span>
      <span className="ccv2-cust-cell-names">
        {c.locked ? lockedLabel : (
          <>
            <span className="ccv2-cust-cell-name">{customerName(c)}</span>
            {c.companyName && <span className="ccv2-cust-cell-sub">{c.companyName}</span>}
          </>
        )}
      </span>
    </div>
  );

  const Contact = ({c}) => (c.locked ? null : (
    <div className="ccv2-cust-cell-contact" onClick={(e) => e.stopPropagation()}>
      {c.email && <a href={`mailto:${c.email}`}>{c.email}</a>}
      {c.phone && <a href={`tel:${c.phone}`}>{c.phone}</a>}
      {!c.email && !c.phone && <span className="ccv2-cust-muted">-</span>}
    </div>
  ));

  const Rides = ({c}) => (c.locked ? null : (
    <div className="ccv2-cust-cell-rides">
      <span className="ccv2-cust-rides-count">{c.stats.ridesCompleted}</span>
      {c.stats.ridesUpcoming > 0 && (
        <span className="ccv2-cust-upcoming">
          <FormattedMessage id="X_UPCOMING" values={{count: c.stats.ridesUpcoming}}/>
        </span>
      )}
    </div>
  ));

  const SortableHeader = ({column, labelId, className}) => {
    const isActive = sortBy === column;
    const icon = !isActive ? faSort : (sortOrder === 'asc' ? faSortUp : faSortDown);
    return (
      <th className={`ccv2-sortable ${isActive ? 'is-active' : ''} ${className || ''}`} onClick={() => handleSort(column)}
          title={intl.formatMessage({id: 'SORT_COLUMN'})}>
        <FormattedMessage id={labelId}/>
        <FontAwesomeIcon icon={icon} className="ccv2-sort-icon"/>
      </th>
    );
  };

  const desktopActions = (c) => (c.locked ? null : (
    <div className="ccv2-crud-actions">
      {actionsFor(c).filter(a => a.key !== 'details').map(a => (
        <OverlayTrigger key={a.key} trigger={HOVER} overlay={<Tooltip>{intl.formatMessage({id: a.labelId})}</Tooltip>}>
          <button type="button" className={`ccv2-crud-iconbtn ${intentClass(a.intent)}`}
                  aria-label={intl.formatMessage({id: a.labelId})}
                  onClick={(e) => { e.stopPropagation(); a.onClick(); }}>
            <FontAwesomeIcon icon={a.icon}/>
          </button>
        </OverlayTrigger>
      ))}
    </div>
  ));

  const TableRow = ({c}) => (
    <tr onClick={() => openDetail(c)} style={{cursor: 'pointer'}} className={c.locked ? 'ccv2-booking-blurred' : ''}>
      <td className="ccv2-cust-td-identity"><Identity c={c}/></td>
      <td className="ccv2-cust-td-left"><Contact c={c}/></td>
      <td><Rides c={c}/></td>
      <td className="col-hide-lg">{c.locked ? null : (formatDate(c.stats.lastRideAt, intl.locale) || <span className="ccv2-cust-muted">-</span>)}</td>
      <td className="is-numeric">{c.locked ? null : formatMoney(c.stats.totalSpent, currency)}</td>
      <td className="col-hide-xl">{formatDate(c.createdAt, intl.locale)}</td>
      <td onClick={(e) => e.stopPropagation()}>{desktopActions(c)}</td>
    </tr>
  );

  const MobileCard = ({c}) => (
    <div className={`ccv2-crud-mobile-card ${c.locked ? 'ccv2-booking-blurred' : ''}`} onClick={() => openDetail(c)}>
      <div className="ccv2-crud-mc-head">
        <div className="ccv2-crud-mc-title"><Identity c={c}/></div>
      </div>
      {!c.locked && (
        <>
          <div className="ccv2-crud-mv-row">
            <span className="ccv2-crud-mv-label"><FormattedMessage id="CONTACT"/></span>
            <span className="ccv2-crud-mv-value"><Contact c={c}/></span>
          </div>
          <div className="ccv2-crud-mv-row">
            <span className="ccv2-crud-mv-label"><FormattedMessage id="RIDES_COMPLETED"/></span>
            <span className="ccv2-crud-mv-value"><Rides c={c}/></span>
          </div>
          <div className="ccv2-crud-mv-row">
            <span className="ccv2-crud-mv-label"><FormattedMessage id="LAST_RIDE"/></span>
            <span className="ccv2-crud-mv-value">{formatDate(c.stats.lastRideAt, intl.locale) || '-'}</span>
          </div>
          <div className="ccv2-crud-mv-row">
            <span className="ccv2-crud-mv-label"><FormattedMessage id="TOTAL_SPENT"/></span>
            <span className="ccv2-crud-mv-value is-numeric">{formatMoney(c.stats.totalSpent, currency)}</span>
          </div>
          <div className="ccv2-crud-mobile-actions" onClick={(e) => e.stopPropagation()}>
            {actionsFor(c).map(a => (
              <button key={a.key} type="button" className={`ccv2-crud-mbtn ${intentClass(a.intent)}`} onClick={a.onClick}>
                <FontAwesomeIcon icon={a.icon}/> <span>{intl.formatMessage({id: a.labelId})}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const Kpi = ({icon, labelId, value, hintId}) => (
    <div className="ccv2-cust-kpi">
      <span className="ccv2-cust-kpi-icon"><FontAwesomeIcon icon={icon}/></span>
      <div>
        <div className="ccv2-cust-kpi-value">{value}</div>
        <div className="ccv2-cust-kpi-label"><FormattedMessage id={labelId}/></div>
        {hintId && <div className="ccv2-cust-kpi-hint"><FormattedMessage id={hintId}/></div>}
      </div>
    </div>
  );

  const paginationItems = [];
  for (let number = 1; number <= totalPages; number++) {
    if (totalPages > 7 && Math.abs(number - currentPage) > 2 && number !== 1 && number !== totalPages) {
      if (number === 2 || number === totalPages - 1) {
        paginationItems.push(<Pagination.Ellipsis key={`dots-${number}`} disabled/>);
      }
      continue;
    }
    paginationItems.push(
      <Pagination.Item key={number} active={number === currentPage} onClick={() => setPage(number)}>{number}</Pagination.Item>
    );
  }

  const loading = customers === null;
  const isEmpty = !loading && customers.length === 0;

  return (
    <div className="ccv2-crud ccv2-customers">
      <PageHeader title="CUSTOMERS" description="CUSTOMERS_DESC"/>

      <div className="ccv2-cust-kpis">
        <Kpi icon={faUsers} labelId="KPI_CUSTOMERS" value={kpis.total}/>
        <Kpi icon={faUserPlus} labelId="KPI_NEW_THIS_MONTH" value={kpis.newThisMonth}/>
        <Kpi icon={faUserCheck} labelId="KPI_REGULAR_CUSTOMERS" value={kpis.regular} hintId="KPI_REGULAR_HINT"/>
        <Kpi icon={faCoins} labelId="KPI_AVERAGE_REVENUE" value={formatMoney(kpis.averageRevenue, currency)}
             hintId="KPI_AVERAGE_REVENUE_HINT"/>
      </div>

      <div className="ccv2-toolbar ccv2-toolbar-spread">
        <div className="ccv2-toolbar-group">
          <button type="button" className="ccv2-btn ccv2-btn-primary" onClick={() => openForm(null)}>
            <FontAwesomeIcon icon={faPlus}/> <FormattedMessage id="NEW_CUSTOMER"/>
          </button>
        </div>
        <ExportDropdown intl={intl} filename={intl.formatMessage({id: 'CUSTOMERS'})} disabled={isEmpty || loading}
                        request={(format) => API.getCustomersExport(history, format)}/>
      </div>

      {!isEmpty && (
        <div className="ccv2-cust-filters">
          <div className="ccv2-cust-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="ccv2-cust-search-icon"/>
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
                   placeholder={intl.formatMessage({id: 'SEARCH_CUSTOMER_PLACEHOLDER'})}
                   aria-label={intl.formatMessage({id: 'SEARCH_CUSTOMER_PLACEHOLDER'})}/>
            {query && (
              <button type="button" className="ccv2-cust-search-clear" onClick={() => setQuery('')}
                      aria-label={intl.formatMessage({id: 'CLOSE'})}>
                <FontAwesomeIcon icon={faTimes}/>
              </button>
            )}
          </div>
          <div className="ccv2-cust-segments" role="tablist">
            {FILTERS.map(f => (
              <button key={f.key} type="button" role="tab" aria-selected={filter === f.key}
                      className={`ccv2-cust-segment ${filter === f.key ? 'is-active' : ''}`} onClick={() => setFilter(f.key)}>
                <FormattedMessage id={f.labelId}/>
              </button>
            ))}
          </div>
          <span className="ccv2-cust-result-count">
            <FormattedMessage id="CUSTOMERS_COUNT" values={{count: visible.length}}/>
          </span>
        </div>
      )}

      {isEmpty && (
        <div className="ccv2-crud-empty">
          <div className="ccv2-crud-empty-icon"><FontAwesomeIcon icon={faUsers}/></div>
          <div className="ccv2-crud-empty-title"><FormattedMessage id="NO_CUSTOMER_YET"/></div>
          <div className="ccv2-crud-empty-desc"><FormattedMessage id="NO_CUSTOMER_YET_DESC"/></div>
          <button type="button" className="ccv2-btn ccv2-btn-primary" onClick={() => openForm(null)}>
            <FontAwesomeIcon icon={faPlus}/> <FormattedMessage id="NEW_CUSTOMER"/>
          </button>
        </div>
      )}

      {!loading && !isEmpty && visible.length === 0 && (
        <div className="ccv2-crud-empty">
          <div className="ccv2-crud-empty-icon"><FontAwesomeIcon icon={faMagnifyingGlass}/></div>
          <div className="ccv2-crud-empty-desc"><FormattedMessage id="NO_CUSTOMER_MATCH"/></div>
        </div>
      )}

      {!loading && visible.length > 0 && (isMobile ? (
        <>
          <div className="ccv2-crud-mobile-sort">
            <span className="ccv2-crud-sort-label"><FormattedMessage id="SORT_BY"/></span>
            <select value={sortBy} onChange={(e) => handleSort(e.target.value)}>
              {SORT_COLUMNS.map(c => <option key={c.column} value={c.column}>{intl.formatMessage({id: c.labelId})}</option>)}
            </select>
            <button type="button" className="ccv2-crud-sort-dir"
                    onClick={() => setSortOrder(order => (order === 'asc' ? 'desc' : 'asc'))}
                    title={intl.formatMessage({id: 'SORT_DIRECTION'})} aria-label={intl.formatMessage({id: 'SORT_DIRECTION'})}>
              <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortUp : faSortDown}/>
            </button>
          </div>
          <div className="ccv2-crud-mobile-list">
            {pageItems.map(c => <MobileCard key={`customer-${c.id}`} c={c}/>)}
          </div>
        </>
      ) : (
        <div className="ccv2-crud-card">
          <div className="ccv2-crud-scroll">
            <table className="ccv2-crud-table">
              <thead>
              <tr>
                <SortableHeader column="name" labelId="CUSTOMER" className="ccv2-cust-td-identity"/>
                <th className="ccv2-cust-td-left"><FormattedMessage id="CONTACT"/></th>
                <SortableHeader column="rides" labelId="RIDES_COMPLETED"/>
                <SortableHeader column="lastRide" labelId="LAST_RIDE" className="col-hide-lg"/>
                <SortableHeader column="totalSpent" labelId="TOTAL_SPENT"/>
                <SortableHeader column="createdAt" labelId="CUSTOMER_SINCE" className="col-hide-xl"/>
                <th><FormattedMessage id="ACTIONS"/></th>
              </tr>
              </thead>
              <tbody>
              {pageItems.map(c => <TableRow key={`customer-${c.id}`} c={c}/>)}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {totalPages > 1 && (
        <div className="ccv2-crud-pagination">
          <Pagination size="md" className="mt-3">
            <Pagination.Prev onClick={() => setPage(Math.max(1, currentPage - 1))}>
              <FontAwesomeIcon icon={faAngleDoubleLeft}/>
            </Pagination.Prev>
            {paginationItems}
            <Pagination.Next onClick={() => setPage(Math.min(totalPages, currentPage + 1))}>
              <FontAwesomeIcon icon={faAngleDoubleRight}/>
            </Pagination.Next>
          </Pagination>
        </div>
      )}

      <CustomerDetailPanel intl={intl}
                           open={showDetail}
                           customerId={detailId}
                           refreshKey={detailRefreshKey}
                           customers={customers}
                           onClose={closeDetail}
                           onEdit={openForm}
                           onDelete={setDeleteTarget}
                           onNewBooking={setBookingCustomer}
                           onOpenBooking={openBooking}
                           onMerged={refreshAll}/>

      {showForm && (
        <CustomerFormModal intl={intl} show={showForm} customer={formCustomer}
                           onClose={() => setShowForm(false)} onSaved={handleSaved}/>
      )}

      {bookingCustomer && (
        <BookingCreateModal intl={intl}
                            show={Boolean(bookingCustomer)}
                            initialCustomer={bookingCustomer}
                            onClose={() => setBookingCustomer(null)}
                            onRefresh={refreshAll}/>
      )}

      {focusedBooking && (
        <BookingModal intl={intl}
                      lengthUnit={lengthUnit}
                      booking={focusedBooking}
                      show={showFocusedBooking}
                      onClose={() => setShowFocusedBooking(false)}
                      onRefresh={refreshAll}/>
      )}

      <Modal as={Modal.Dialog} centered show={Boolean(deleteTarget)} onHide={() => setDeleteTarget(null)}>
        <Modal.Header>
          <Modal.Title className="h6"><FormattedMessage id="DELETE_CUSTOMER"/></Modal.Title>
          <Button variant="close" aria-label="Close" onClick={() => setDeleteTarget(null)}/>
        </Modal.Header>
        <Modal.Body>
          {deleteTarget && (
            <>
              <p className="fw-bold mb-2">{customerName(deleteTarget)}</p>
              <FormattedMessage id={deleteTarget.stats && deleteTarget.stats.bookingsCount > 0
                ? 'DELETE_CUSTOMER_WITH_BOOKINGS' : 'DELETE_CUSTOMER_NO_BOOKING'}/>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleDelete}><FormattedMessage id="DELETE"/></Button>
          <Button variant="link" className="text-gray ms-auto" onClick={() => setDeleteTarget(null)}>
            <FormattedMessage id="CLOSE"/>
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
